/* ============================================
   PHOTO EDITOR - Fabric.js Based
   Smaller canvas, default image smaller for resize handles
   ============================================ */

(function () {
  'use strict';

  const STORAGE_KEY = 'pe_customization_';

  let canvas = null;
  let currentProductHandle = '';
  let currentProductUrl = '';
  let currentFrameType = 'simple';
  let currentPhotoSlots  = 1;
  let currentPhotoLayout = 'single';
  let currentSlotGeometry = ''; // "cx,cy,w,h,shape|..."
  let currentTemplateImage = ''; // URL of the official product template
  let currentPersonalizationFields = []; // array of metaobject-driven form field defs
  let personalizationValues = {};        // {key: value} — populated as user fills inputs
  let compositeSlotEls = []; // DOM refs to composite slot inner divs
  let currentBorderColor = '#000000';
  let currentOrientation = 'portrait';
  let uploadedImageObj = null;
  let hasPhoto = false;

  // Canvas size — must fit the FRAME (canvas + border + matting + controls)
  // within both viewport width and viewport height. The frame visual is wider
  // than the canvas by ~20px each side (mat/border), plus modal padding ~16px
  // each side, plus the toolbar/controls below the canvas (~140px tall).
  function getCanvasSizes() {
    const vw = Math.min(window.innerWidth || 800, document.documentElement.clientWidth || 800);
    const vh = Math.min(window.innerHeight || 800, document.documentElement.clientHeight || 800);
    // Width budget: viewport - modal padding (32) - frame border (40) - safety (24)
    const widthBudget  = Math.max(200, vw - 100);
    // Height budget: viewport - top bar (~60) - toolbar (~150) - frame border (40) - safety (20)
    const heightBudget = Math.max(200, vh - 270);
    // The long side of the canvas can't exceed either budget.
    const maxW = Math.max(220, Math.min(widthBudget, heightBudget, 420));
    // Long side : short side = 1.36 (≈ original 380:280)
    const shortSide = Math.round(maxW / 1.36);
    return {
      portrait:  { width: shortSide, height: maxW },
      landscape: { width: maxW,      height: shortSide },
    };
  }
  let CANVAS_SIZES = getCanvasSizes();

  const $ = (sel) => document.querySelector(sel);
  const $$ = (sel) => document.querySelectorAll(sel);

  function init() {
    bindCardButtons();
    bindModalControls();
    bindToolbar();
    bindPanels();
    bindOrientationButtons();
    bindSaveButton();
  }

  // ---- CARD BUTTONS ----
  function bindCardButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-open-photo-editor]');
      if (!btn) return;
      e.preventDefault();

      currentProductHandle = btn.dataset.productHandle || '';
      currentProductUrl = btn.dataset.productUrl || '';
      currentFrameType = btn.dataset.frameType || 'simple';
      // Multi-photo products (hexa collage, 5+1 collage, 12-photo clock, etc.)
      currentPhotoSlots  = parseInt(btn.dataset.photoSlots  || '1', 10) || 1;
      currentPhotoLayout = btn.dataset.photoLayout || 'single';
      currentSlotGeometry = btn.dataset.slotGeometry || '';
      currentTemplateImage = btn.dataset.templateImage || '';
      // Personalization fields (metaobject-driven form inputs)
      try {
        currentPersonalizationFields = JSON.parse(btn.dataset.personalizationFields || '[]');
        if (!Array.isArray(currentPersonalizationFields)) currentPersonalizationFields = [];
      } catch (err) {
        currentPersonalizationFields = [];
      }
      openEditor();
    });
  }

  // ---- MODAL ----
  function bindModalControls() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-pe-close]')) closeEditor();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && $('#photo-editor-modal')?.classList.contains('pe-modal--open')) closeEditor();
    });
  }

  function openEditor() {
    const modal = $('#photo-editor-modal');
    if (!modal) return;

    modal.classList.add('pe-modal--open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Reflect required photo count in the modal
    applyMultiPhotoUI(currentPhotoSlots, currentPhotoLayout);

    // Render metaobject-driven personalization fields (empty → hidden)
    renderPersonalizationFields(currentPersonalizationFields);

    setTimeout(() => {
      initCanvas();
      loadSavedState();
    }, 50);
  }

  // ---- PERSONALIZATION FIELDS (metaobject-driven) ----
  function renderPersonalizationFields(fields) {
    const host = document.getElementById('pe-personalization');
    if (!host) return;
    host.innerHTML = '';
    personalizationValues = {};
    if (!fields || fields.length === 0) {
      host.classList.remove('pe-pers--active');
      return;
    }
    // Sort by display_order (0 default = first)
    const sorted = fields.slice().sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const h = document.createElement('h4');
    h.className = 'pe-pers__heading';
    h.textContent = 'Personalization Details';
    host.appendChild(h);
    sorted.forEach((f) => {
      const wrap = document.createElement('div');
      wrap.className = 'pe-pers__field';
      wrap.dataset.key = f.key;
      wrap.dataset.type = f.input_type;
      wrap.appendChild(buildInputBlock(f));
      host.appendChild(wrap);
    });
    host.classList.add('pe-pers--active');
  }

  function buildInputBlock(f) {
    const frag = document.createDocumentFragment();
    const inputType = f.input_type || 'text';
    // Label (skip for checkbox — inline)
    if (inputType !== 'checkbox') {
      const lbl = document.createElement('label');
      lbl.className = 'pe-pers__label' + (f.required ? ' pe-pers__label--req' : '');
      lbl.textContent = f.label || f.key;
      lbl.htmlFor = 'pe-pers-' + f.key;
      frag.appendChild(lbl);
    }
    let input;
    switch (inputType) {
      case 'textarea':
        input = document.createElement('textarea');
        input.className = 'pe-pers__textarea';
        if (f.placeholder) input.placeholder = f.placeholder;
        if (f.max_length > 0) input.maxLength = f.max_length;
        input.value = f.default_value || '';
        break;
      case 'select':
        input = document.createElement('select');
        input.className = 'pe-pers__select';
        input.appendChild(new Option('-- select --', ''));
        parseOptions(f.options_json).forEach(opt => input.appendChild(new Option(opt.label, opt.value)));
        if (f.default_value) input.value = f.default_value;
        break;
      case 'multiselect': {
        const container = document.createElement('div');
        container.className = 'pe-pers__multi';
        const state = new Set();
        parseOptions(f.options_json).forEach(opt => {
          const chip = document.createElement('button');
          chip.type = 'button'; chip.className = 'pe-pers__multi-opt';
          chip.textContent = opt.label; chip.dataset.value = opt.value;
          chip.addEventListener('click', () => {
            if (state.has(opt.value)) { state.delete(opt.value); chip.classList.remove('pe-pers__multi-opt--on'); }
            else { state.add(opt.value); chip.classList.add('pe-pers__multi-opt--on'); }
            personalizationValues[f.key] = Array.from(state).join(', ');
          });
          container.appendChild(chip);
        });
        input = container;
        break;
      }
      case 'checkbox': {
        const row = document.createElement('label');
        row.className = 'pe-pers__checkbox-row';
        const cb = document.createElement('input');
        cb.type = 'checkbox'; cb.id = 'pe-pers-' + f.key;
        cb.checked = (f.default_value === 'true' || f.default_value === '1');
        const span = document.createElement('span');
        span.className = 'pe-pers__label';
        span.textContent = f.label || f.key;
        if (f.required) span.className += ' pe-pers__label--req';
        row.appendChild(cb); row.appendChild(span);
        cb.addEventListener('change', () => { personalizationValues[f.key] = cb.checked ? 'Yes' : ''; });
        frag.appendChild(row);
        if (f.help_text) { const hp = document.createElement('span'); hp.className = 'pe-pers__help'; hp.textContent = f.help_text; frag.appendChild(hp); }
        const err = document.createElement('span'); err.className = 'pe-pers__err'; frag.appendChild(err);
        return frag;
      }
      case 'color': {
        // If options_json present, render swatch palette; else HTML color picker
        const opts = parseOptions(f.options_json);
        if (opts.length > 0) {
          const container = document.createElement('div');
          container.className = 'pe-pers__multi';
          opts.forEach(opt => {
            const sw = document.createElement('button');
            sw.type = 'button';
            sw.className = 'pe-pers__multi-opt';
            sw.style.background = opt.value;
            sw.title = opt.label;
            sw.dataset.value = opt.value;
            sw.addEventListener('click', () => {
              container.querySelectorAll('.pe-pers__multi-opt--on').forEach(el => el.classList.remove('pe-pers__multi-opt--on'));
              sw.classList.add('pe-pers__multi-opt--on');
              personalizationValues[f.key] = opt.value;
            });
            container.appendChild(sw);
          });
          input = container;
        } else {
          input = document.createElement('input');
          input.type = 'color'; input.className = 'pe-pers__input';
          input.value = f.default_value || '#000000';
        }
        break;
      }
      case 'photo':
      case 'video': {
        const btn = document.createElement('label');
        btn.className = 'pe-pers__file-btn';
        btn.textContent = inputType === 'photo' ? '📷 Choose image' : '🎥 Choose video';
        const fi = document.createElement('input');
        fi.type = 'file';
        fi.accept = f.accept_types || (inputType === 'photo' ? 'image/*' : 'video/*');
        const nameSpan = document.createElement('span');
        nameSpan.className = 'pe-pers__file-name';
        const maxMB = f.max_file_size_mb > 0 ? f.max_file_size_mb : (inputType === 'photo' ? 5 : 25);
        fi.addEventListener('change', () => {
          const file = fi.files && fi.files[0];
          if (!file) return;
          // Validate MIME
          const okType = inputType === 'photo' ? file.type.startsWith('image/') : file.type.startsWith('video/');
          if (!okType) { nameSpan.textContent = 'Wrong file type'; personalizationValues[f.key] = ''; return; }
          // Validate size
          if (file.size > maxMB * 1024 * 1024) {
            nameSpan.textContent = `File too large (max ${maxMB}MB)`;
            personalizationValues[f.key] = '';
            fi.value = '';
            return;
          }
          nameSpan.textContent = file.name;
          personalizationValues[f.key] = file.name; // filename is what goes to cart property; actual upload needs a separate step
        });
        btn.appendChild(fi);
        const wrap = document.createDocumentFragment();
        wrap.appendChild(btn); wrap.appendChild(nameSpan);
        frag.appendChild(wrap);
        const helpText = document.createElement('span');
        helpText.className = 'pe-pers__help';
        helpText.textContent = (f.help_text || '') + (f.help_text ? ' · ' : '') + `Max ${maxMB}MB, ${inputType === 'photo' ? 'images' : 'videos'} only`;
        frag.appendChild(helpText);
        const err = document.createElement('span'); err.className = 'pe-pers__err'; frag.appendChild(err);
        return frag;
      }
      case 'repeatable_group': {
        const container = document.createElement('div');
        const rows = [];
        const minRows = Math.max(1, f.repeatable_min || 1);
        const maxRows = Math.max(minRows, f.repeatable_max || 10);
        function syncValue() {
          personalizationValues[f.key] = rows.map(r => r.value).filter(Boolean).join(' | ');
        }
        function addRow() {
          if (rows.length >= maxRows) return;
          const row = document.createElement('div');
          row.className = 'pe-pers__repeat-row';
          const inp = document.createElement('input');
          inp.type = 'text'; inp.className = 'pe-pers__input';
          if (f.placeholder) inp.placeholder = f.placeholder;
          if (f.max_length > 0) inp.maxLength = f.max_length;
          inp.addEventListener('input', () => { rows[rows.indexOf(inp)] = inp; syncValue(); });
          const rm = document.createElement('button');
          rm.type = 'button'; rm.className = 'pe-pers__repeat-btn pe-pers__repeat-btn--remove'; rm.textContent = '−';
          rm.addEventListener('click', () => {
            if (rows.length <= minRows) return;
            const idx = rows.indexOf(inp); rows.splice(idx, 1); row.remove(); syncValue();
          });
          row.appendChild(inp); row.appendChild(rm);
          container.appendChild(row); rows.push(inp);
        }
        for (let i = 0; i < minRows; i++) addRow();
        const addBtn = document.createElement('button');
        addBtn.type = 'button'; addBtn.className = 'pe-pers__repeat-btn'; addBtn.textContent = '+ Add another';
        addBtn.addEventListener('click', addRow);
        container.appendChild(addBtn);
        input = container;
        break;
      }
      case 'date': case 'number': case 'email': case 'phone': case 'text': default: {
        input = document.createElement('input');
        input.className = 'pe-pers__input';
        input.type = inputType === 'phone' ? 'tel' : (inputType === 'date' ? 'date' : (inputType === 'number' ? 'number' : (inputType === 'email' ? 'email' : 'text')));
        if (f.placeholder) input.placeholder = f.placeholder;
        if (f.max_length > 0 && (inputType === 'text' || inputType === 'email' || inputType === 'phone')) input.maxLength = f.max_length;
        if (inputType === 'number' || inputType === 'date') {
          if (f.min_value !== null && f.min_value !== undefined) input.min = f.min_value;
          if (f.max_value !== null && f.max_value !== undefined) input.max = f.max_value;
        }
        if (f.validation_pattern) input.pattern = f.validation_pattern;
        input.value = f.default_value || '';
      }
    }
    input.id = 'pe-pers-' + f.key;
    input.addEventListener('input', () => {
      let v = (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA' || input.tagName === 'SELECT') ? input.value : '';
      personalizationValues[f.key] = v;
    });
    frag.appendChild(input);
    // Character counter for text/textarea with max_length
    if ((inputType === 'text' || inputType === 'textarea') && f.max_length > 0) {
      const counter = document.createElement('span');
      counter.className = 'pe-pers__counter';
      const updateCounter = () => { counter.textContent = `${(input.value || '').length}/${f.max_length}`; };
      updateCounter();
      input.addEventListener('input', updateCounter);
      frag.appendChild(counter);
    }
    if (f.help_text) {
      const hp = document.createElement('span');
      hp.className = 'pe-pers__help';
      hp.textContent = f.help_text;
      frag.appendChild(hp);
    }
    const err = document.createElement('span');
    err.className = 'pe-pers__err';
    err.textContent = f.validation_message || 'Please check this field.';
    frag.appendChild(err);
    // Initialize value from default
    if (f.default_value) personalizationValues[f.key] = f.default_value;
    return frag;
  }

  function parseOptions(raw) {
    if (!raw) return [];
    try {
      const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(arr) ? arr.filter(o => o && o.value !== undefined).map(o => ({ value: String(o.value), label: String(o.label || o.value) })) : [];
    } catch (e) { return []; }
  }

  function validatePersonalizationFields() {
    const host = document.getElementById('pe-personalization');
    if (!host || !host.classList.contains('pe-pers--active')) return true;
    let ok = true;
    currentPersonalizationFields.forEach(f => {
      const wrap = host.querySelector(`.pe-pers__field[data-key="${f.key}"]`);
      if (!wrap) return;
      wrap.classList.remove('pe-pers__field--invalid');
      const val = (personalizationValues[f.key] || '').trim();
      if (f.required && !val) { wrap.classList.add('pe-pers__field--invalid'); ok = false; return; }
      if (val && f.validation_pattern) {
        try {
          const re = new RegExp(f.validation_pattern);
          if (!re.test(val)) { wrap.classList.add('pe-pers__field--invalid'); ok = false; return; }
        } catch (e) { /* invalid regex → skip */ }
      }
    });
    return ok;
  }

  /* Multi-photo mode UI
     - Updates the modal title to show "N of X" progress
     - Adds a slot-strip beneath the upload prompt
     - Photo editor still edits ONE photo at a time; when saved, the next slot
       becomes active until all N are filled. */
  let uploadedSlots = [];  // array of dataURL thumbnails, length up to currentPhotoSlots
  let activeSlotIndex = 0;

  function applyMultiPhotoUI(slots, layout) {
    uploadedSlots = new Array(slots).fill(null);
    activeSlotIndex = 0;
    const title = $('.pe-modal__title');
    if (title) title.textContent = 'Customize Your Photo' + (slots > 1 ? 's' : '');
    const promptText = $('.pe-upload-prompt__text');
    if (promptText) promptText.textContent = slots > 1
      ? 'SELECT ' + slots + ' PHOTOS'
      : 'SELECT PHOTO';
    const promptHint = $('.pe-upload-prompt__hint');
    if (promptHint) promptHint.style.display = slots > 1 ? 'block' : 'none';
    const uploadGroup = $('.pe-toolbar__group');
    if (uploadGroup) uploadGroup.style.display = 'none';

    // Composite view for multi-photo products; single-photo keeps the canvas.
    const composite = $('#pe-composite');
    const frame     = $('#pe-frame');
    if (slots > 1 && currentSlotGeometry) {
      renderComposite(currentSlotGeometry);
      if (composite) composite.style.display = 'block';
      if (frame)     frame.style.display = 'none';
      const slotStrip = $('#pe-slot-strip');
      if (slotStrip) slotStrip.remove(); // legacy strip not needed
    } else {
      if (composite) composite.style.display = 'none';
      if (frame)     frame.style.display = '';
      renderSlotStrip(slots, layout);
    }
  }

  /* Build the composite view:
     - If a template image URL is set on the metafield, we display it as the
       backdrop (the official OMGs-style product mockup with placeholders baked
       into the image).
     - Slot positions from slot_geometry become interactive overlay areas —
       transparent until the customer uploads a photo, at which point the
       photo is placed inside the slot on top of the template image.  */
  function renderComposite(geometry) {
    const host = $('#pe-composite');
    if (!host) return;
    host.innerHTML = '';
    compositeSlotEls = [];

    // Backdrop = official product template image (metafield-driven)
    if (currentTemplateImage) {
      const bg = document.createElement('img');
      bg.className = 'pe-composite__template';
      bg.src = currentTemplateImage;
      bg.alt = '';
      host.appendChild(bg);
    }

    const entries = geometry.split('|');
    entries.forEach((entry, idx) => {
      const parts = entry.split(',').map(s => s.trim());
      const cx = parts[0], cy = parts[1], w = parts[2], h = parts[3];
      const shape = (parts[4] || 'rect').toLowerCase();
      const slot = document.createElement('div');
      slot.className = 'pe-composite__slot pe-composite__slot--' + shape;
      // When a template image is present, the slots are TRANSPARENT overlays —
      // the visible frame edges + glossy panels are in the template image itself.
      if (currentTemplateImage) slot.classList.add('pe-composite__slot--overlay');
      slot.setAttribute('data-slot-index', String(idx));
      slot.style.cssText =
        'left:' + cx + '%;top:' + cy + '%;width:' + w + '%;height:' + h + '%;';
      const inner = document.createElement('div');
      inner.className = 'pe-composite__inner';
      const label = document.createElement('span');
      label.className = 'pe-composite__label';
      label.textContent = 'Photo ' + (idx + 1);
      label.style.whiteSpace = 'pre-line';
      inner.appendChild(label);
      slot.appendChild(inner);
      slot.addEventListener('click', () => selectCompositeSlot(idx));
      host.appendChild(slot);
      compositeSlotEls.push(inner);
    });
    highlightCompositeSlot(0);
  }

  function highlightCompositeSlot(idx) {
    const host = $('#pe-composite');
    if (!host) return;
    host.querySelectorAll('.pe-composite__slot').forEach((el, i) => {
      el.classList.toggle('pe-composite__slot--active', i === idx);
    });
  }

  function selectCompositeSlot(idx) {
    activeSlotIndex = idx;
    highlightCompositeSlot(idx);
    // If empty, open file picker directly
    if (!uploadedSlots[idx]) {
      const input = $('#pe-file-input');
      if (input) { input.multiple = false; input.click(); }
    }
  }

  function paintCompositeSlot(idx, src) {
    const inner = compositeSlotEls[idx];
    if (!inner) return;
    inner.innerHTML = '';
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    // Store per-slot transform state directly on the img element
    img.dataset.tx = '0'; img.dataset.ty = '0'; img.dataset.scale = '1';
    img.style.transformOrigin = '50% 50%';
    updateSlotImgTransform(img);
    inner.appendChild(img);
    attachSlotGestures(inner, img);
  }

  function updateSlotImgTransform(img) {
    const tx = parseFloat(img.dataset.tx || '0');
    const ty = parseFloat(img.dataset.ty || '0');
    const sc = parseFloat(img.dataset.scale || '1');
    img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + sc + ')';
  }

  function attachSlotGestures(inner, img) {
    let dragging = false;
    let lastX = 0, lastY = 0;
    inner.addEventListener('mousedown', function (e) {
      dragging = true;
      lastX = e.clientX; lastY = e.clientY;
      e.preventDefault();
    });
    document.addEventListener('mousemove', function (e) {
      if (!dragging) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      img.dataset.tx = String((parseFloat(img.dataset.tx || '0') + dx));
      img.dataset.ty = String((parseFloat(img.dataset.ty || '0') + dy));
      updateSlotImgTransform(img);
    });
    document.addEventListener('mouseup', function () { dragging = false; });
    inner.addEventListener('wheel', function (e) {
      e.preventDefault();
      const cur = parseFloat(img.dataset.scale || '1');
      const next = Math.max(0.5, Math.min(4, cur * (e.deltaY < 0 ? 1.08 : 0.92)));
      img.dataset.scale = String(next);
      updateSlotImgTransform(img);
    }, { passive: false });

    // Touch — one-finger pan, two-finger pinch
    let touchStartDist = 0;
    let baseScale = 1;
    inner.addEventListener('touchstart', function (e) {
      if (e.touches.length === 1) {
        dragging = true;
        lastX = e.touches[0].clientX; lastY = e.touches[0].clientY;
      } else if (e.touches.length === 2) {
        dragging = false;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        touchStartDist = Math.hypot(dx, dy);
        baseScale = parseFloat(img.dataset.scale || '1');
      }
    }, { passive: true });
    inner.addEventListener('touchmove', function (e) {
      if (e.touches.length === 1 && dragging) {
        const dx = e.touches[0].clientX - lastX;
        const dy = e.touches[0].clientY - lastY;
        lastX = e.touches[0].clientX; lastY = e.touches[0].clientY;
        img.dataset.tx = String((parseFloat(img.dataset.tx || '0') + dx));
        img.dataset.ty = String((parseFloat(img.dataset.ty || '0') + dy));
        updateSlotImgTransform(img);
      } else if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const d = Math.hypot(dx, dy);
        const ratio = d / (touchStartDist || 1);
        const next = Math.max(0.5, Math.min(4, baseScale * ratio));
        img.dataset.scale = String(next);
        updateSlotImgTransform(img);
      }
    }, { passive: true });
    inner.addEventListener('touchend', function () { dragging = false; });
  }

  function renderSlotStrip(slots, layout) {
    const modalContainer = $('.pe-modal__container');
    if (!modalContainer) return;
    let strip = $('#pe-slot-strip');
    if (slots <= 1) { if (strip) strip.remove(); return; }
    if (!strip) {
      strip = document.createElement('div');
      strip.id = 'pe-slot-strip';
      strip.style.cssText = 'display:flex;gap:8px;padding:10px 16px;overflow-x:auto;border-top:1px solid #f0f0f0;background:#fafafa;flex-shrink:0;';
      const header = $('.pe-modal__header');
      if (header && header.parentNode) header.parentNode.insertBefore(strip, header.nextSibling);
    }
    strip.innerHTML = '';
    strip.setAttribute('data-layout', layout);
    for (let i = 0; i < slots; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('data-slot', String(i));
      const filled = !!uploadedSlots[i];
      const active = i === activeSlotIndex;
      btn.style.cssText = 'position:relative;flex:0 0 48px;width:48px;height:48px;border-radius:8px;border:2px solid ' +
        (active ? 'var(--pe-accent,#8B2500)' : (filled ? '#a3b18a' : '#e5e5e5')) +
        ';background:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;overflow:hidden;font-weight:700;font-size:13px;color:#666;padding:0;';
      if (filled) {
        const img = document.createElement('img');
        img.src = uploadedSlots[i];
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
        btn.appendChild(img);
      } else {
        btn.textContent = i + 1;
      }
      btn.addEventListener('click', () => switchSlot(i));
      strip.appendChild(btn);
    }
  }

  function switchSlot(idx) {
    // Save current slot's canvas state before switching
    if (canvas && uploadedImageObj) {
      try { uploadedSlots[activeSlotIndex] = canvas.toDataURL({ format: 'jpeg', quality: 0.85, multiplier: 0.5 }); } catch (_) {}
    }
    activeSlotIndex = idx;
    // Update UI labels
    const title = $('.pe-modal__title');
    if (title) title.textContent = 'Customize Photo ' + (idx + 1) + ' of ' + currentPhotoSlots;
    const promptText = $('.pe-upload-prompt__text');
    if (promptText) promptText.textContent = 'SELECT PHOTO ' + (idx + 1) + ' OF ' + currentPhotoSlots;
    renderSlotStrip(currentPhotoSlots, currentPhotoLayout);
    // Load the newly-selected slot's saved state
    if (canvas) { canvas.clear(); canvas.renderAll(); }
    uploadedImageObj = null;
    const savedThumb = uploadedSlots[idx];
    if (savedThumb) {
      fabric.Image.fromURL(savedThumb, (img) => {
        uploadedImageObj = img;
        fitImageSmall(img);
        canvas.add(img);
        canvas.sendToBack(img);
        canvas.renderAll();
      });
      hideUploadPrompt();
    } else {
      showUploadPrompt();
    }
  }

  function closeEditor() {
    const modal = $('#photo-editor-modal');
    if (!modal) return;

    modal.classList.remove('pe-modal--open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    hideAllPanels();

    if (canvas) { canvas.dispose(); canvas = null; }
    hasPhoto = false;
    uploadedImageObj = null;
  }

  // ---- CANVAS ----
  function initCanvas() {
    const canvasEl = $('#pe-canvas');
    if (!canvasEl) return;

    CANVAS_SIZES = getCanvasSizes();
    const size = CANVAS_SIZES[currentOrientation];

    canvas = new fabric.Canvas('pe-canvas', {
      width: size.width,
      height: size.height,
      backgroundColor: '#f0f0f0',
      selection: true,
      preserveObjectStacking: true,
    });

    applyFrameStyle(currentFrameType);
    applyBorderColor(currentBorderColor);
    showUploadPrompt();
    addEditorLogo();

    canvas.on('selection:created', onObjectSelected);
    canvas.on('selection:updated', onObjectSelected);
    canvas.on('selection:cleared', onObjectDeselected);

    // Canva-style snap behavior — move/rotate snap with guide lines
    canvas.on('object:moving', onObjectMoving);
    canvas.on('object:rotating', onObjectRotating);
    canvas.on('object:modified', clearGuideLines);
    canvas.on('mouse:up', clearGuideLines);
  }

  // ---- SNAP & GUIDE LINES (Canva-like) ----
  const SNAP_THRESHOLD = 6; // px tolerance for position snap
  const ROTATE_SNAP_STEP = 45; // snap at 0°, 45°, 90°, ...
  const ROTATE_SNAP_TOL = 6; // ± degrees

  function attachSnap(obj) {
    if (!obj) return;
    obj.snapAngle = ROTATE_SNAP_STEP;
    obj.snapThreshold = ROTATE_SNAP_TOL;
    obj.centeredRotation = true;
  }

  function isSnapTarget(o) {
    return o && !o.peLogo && !o.peGuide && o.visible !== false;
  }

  function getAlignmentTargets(activeObj) {
    const cw = canvas.getWidth();
    const ch = canvas.getHeight();
    // Canvas centerlines + edges
    const xs = [{ pos: cw / 2, type: 'center' }, { pos: 0, type: 'edge' }, { pos: cw, type: 'edge' }];
    const ys = [{ pos: ch / 2, type: 'center' }, { pos: 0, type: 'edge' }, { pos: ch, type: 'edge' }];
    // Other objects' centers + bounding-box edges
    canvas.getObjects().forEach((o) => {
      if (o === activeObj || !isSnapTarget(o)) return;
      const c = o.getCenterPoint();
      const bb = o.getBoundingRect(true, true);
      xs.push({ pos: c.x, type: 'other-center' });
      xs.push({ pos: bb.left, type: 'other-edge' });
      xs.push({ pos: bb.left + bb.width, type: 'other-edge' });
      ys.push({ pos: c.y, type: 'other-center' });
      ys.push({ pos: bb.top, type: 'other-edge' });
      ys.push({ pos: bb.top + bb.height, type: 'other-edge' });
    });
    return { xs, ys };
  }

  function onObjectMoving(e) {
    const obj = e.target;
    if (!obj || obj.peLogo || obj.peGuide) return;

    const c = obj.getCenterPoint();
    const bb = obj.getBoundingRect(true, true);
    const { xs, ys } = getAlignmentTargets(obj);

    // Source points on the moving object that can snap
    const objXs = [
      { pos: c.x, kind: 'center' },
      { pos: bb.left, kind: 'edge' },
      { pos: bb.left + bb.width, kind: 'edge' },
    ];
    const objYs = [
      { pos: c.y, kind: 'center' },
      { pos: bb.top, kind: 'edge' },
      { pos: bb.top + bb.height, kind: 'edge' },
    ];

    let snapX = null;
    let snapY = null;

    for (const t of xs) {
      for (const s of objXs) {
        const d = t.pos - s.pos;
        if (Math.abs(d) <= SNAP_THRESHOLD) {
          obj.set('left', obj.left + d);
          snapX = t.pos;
          break;
        }
      }
      if (snapX !== null) break;
    }

    for (const t of ys) {
      for (const s of objYs) {
        const d = t.pos - s.pos;
        if (Math.abs(d) <= SNAP_THRESHOLD) {
          obj.set('top', obj.top + d);
          snapY = t.pos;
          break;
        }
      }
      if (snapY !== null) break;
    }

    drawGuideLines(snapX, snapY);
  }

  function onObjectRotating(e) {
    const obj = e.target;
    if (!obj || obj.peLogo || obj.peGuide) return;
    // Snap rotation to nearest multiple of ROTATE_SNAP_STEP within tolerance
    const a = ((obj.angle % 360) + 360) % 360;
    const nearest = Math.round(a / ROTATE_SNAP_STEP) * ROTATE_SNAP_STEP;
    if (Math.abs(a - nearest) <= ROTATE_SNAP_TOL) {
      obj.set('angle', nearest % 360);
    }
  }

  function drawGuideLines(snapX, snapY) {
    clearGuideLines();
    if (snapX !== null) addGuide('vertical', snapX);
    if (snapY !== null) addGuide('horizontal', snapY);
  }

  function addGuide(orient, pos) {
    const cw = canvas.getWidth();
    const ch = canvas.getHeight();
    const coords = orient === 'vertical'
      ? [pos, 0, pos, ch]
      : [0, pos, cw, pos];
    const line = new fabric.Line(coords, {
      stroke: '#FF2D55',
      strokeWidth: 1,
      strokeDashArray: [4, 4],
      selectable: false,
      evented: false,
      excludeFromExport: true,
      hoverCursor: 'default',
    });
    line.peGuide = true;
    canvas.add(line);
    canvas.bringToFront(line);
  }

  function clearGuideLines() {
    if (!canvas) return;
    canvas.getObjects().filter((o) => o.peGuide).forEach((o) => canvas.remove(o));
    canvas.requestRenderAll && canvas.requestRenderAll();
  }

  // Locked logo at bottom-right — included in saved image
  function addEditorLogo() {
    if (!canvas) return;
    const wrap = $('#pe-canvas-wrap');
    const logoSrc = wrap?.dataset?.editorLogo;
    if (!logoSrc) return;
    fabric.Image.fromURL(logoSrc, (logo) => {
      if (!canvas) return;
      const targetW = Math.min(64, canvas.getWidth() * 0.22);
      const scale = targetW / logo.width;
      logo.set({
        scaleX: scale,
        scaleY: scale,
        left: canvas.getWidth() - 8,
        top: canvas.getHeight() - 8,
        originX: 'right',
        originY: 'bottom',
        selectable: false,
        evented: false,
        hoverCursor: 'default',
        excludeFromExport: false,
        opacity: 0.9,
      });
      logo.peLogo = true;
      canvas.add(logo);
      canvas.bringToFront(logo);
      canvas.renderAll();
    }, { crossOrigin: 'anonymous' });
  }

  function resizeCanvas(orientation) {
    if (!canvas) return;
    CANVAS_SIZES = getCanvasSizes();
    const size = CANVAS_SIZES[orientation];
    canvas.setDimensions({ width: size.width, height: size.height });

    if (uploadedImageObj) fitImageSmall(uploadedImageObj);
    // Re-anchor logo to new bottom-right
    canvas.getObjects().filter((o) => o.peLogo).forEach((o) => canvas.remove(o));
    addEditorLogo();
    canvas.renderAll();
  }

  // ---- IMAGE UPLOAD ----
  function bindFileInputs() {
    const fileInput = $('#pe-file-input');
    const fileChange = $('#pe-file-change');
    if (fileInput) fileInput.addEventListener('change', handleFileUpload);
    if (fileChange) fileChange.addEventListener('change', handleFileUpload);
  }

  function handleFileUpload(e) {
    const files = Array.from(e.target.files || []).filter((f) => f && f.type.startsWith('image/'));
    if (!files.length) return;
    const composite = $('#pe-composite');
    const isComposite = currentPhotoSlots > 1 && composite && composite.style.display !== 'none';

    // Multi-photo composite mode: distribute files across slots
    if (isComposite) {
      let startIdx = activeSlotIndex;
      // If picked more than one file, always distribute from slot 0
      if (files.length > 1) startIdx = 0;
      files.forEach((f, i) => {
        const slotIdx = (startIdx + i) % currentPhotoSlots;
        const fr = new FileReader();
        fr.onload = (evt) => {
          uploadedSlots[slotIdx] = evt.target.result;
          paintCompositeSlot(slotIdx, evt.target.result);
        };
        fr.readAsDataURL(f);
      });
      hideUploadPrompt();
      showChangePhotoBtn();
      // Reset input.multiple so next click can be single-file too
      e.target.multiple = true;
      e.target.value = '';
      return;
    }

    // Single-photo canvas mode (unchanged)
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (evt) => { loadUploadedIntoCanvas(evt.target.result); };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function loadUploadedIntoCanvas(dataUrl) {
    if (!canvas) return;
    fabric.Image.fromURL(dataUrl, (img) => {
      if (uploadedImageObj) canvas.remove(uploadedImageObj);

      uploadedImageObj = img;
      fitImageSmall(img);
      canvas.add(img);
      canvas.sendToBack(img);
      canvas.setActiveObject(img);

      // Keep the overflow preview in sync with EVERY move/scale/rotate/modify
      const sync = () => updateOverflowPreview(img);
      img.on('moving', sync);
      img.on('scaling', sync);
      img.on('rotating', sync);
      img.on('modified', sync);
      sync();

      hasPhoto = true;
      hideUploadPrompt();
      showChangePhotoBtn();
      canvas.renderAll();
    });
  }

  // Fit image WIDTH to canvas; height may overflow (clipped vertically by canvas)
  function fitImageSmall(img) {
    if (!canvas || !img) return;

    const cw = canvas.getWidth();
    const ch = canvas.getHeight();
    const scale = cw / img.width;

    img.set({
      scaleX: scale,
      scaleY: scale,
      left: cw / 2,
      top: ch / 2,
      originX: 'center',
      originY: 'center',
      selectable: true,
      hasControls: true,
      hasBorders: true,
    });

    attachSnap(img);
    updateOverflowPreview(img);
  }

  // Show the full image at low opacity behind the canvas, repositioned/resized dynamically
  // every time the user moves/scales the image so the overflow always matches reality
  function updateOverflowPreview(img) {
    const wrap = $('#pe-canvas-wrap');
    if (!wrap || !img || !canvas) return;
    let preview = $('#pe-overflow-preview');
    if (!preview) {
      preview = document.createElement('img');
      preview.id = 'pe-overflow-preview';
      preview.style.cssText = 'position:absolute;opacity:0.25;pointer-events:none;z-index:0;';
      wrap.insertBefore(preview, wrap.firstChild);
    }
    preview.src = img.getSrc ? img.getSrc() : (img._element ? img._element.src : '');

    const cw = canvas.getWidth();
    const ch = canvas.getHeight();
    const dispW = img.width * (img.scaleX || 1);
    const dispH = img.height * (img.scaleY || 1);
    // image is drawn with originX: 'center', originY: 'center' so left/top are the center
    const imgLeftInCanvas = (img.left ?? cw / 2) - dispW / 2;
    const imgTopInCanvas = (img.top ?? ch / 2) - dispH / 2;

    const frame = $('#pe-frame');
    if (!frame) return;
    const fr = frame.getBoundingClientRect();
    const wr = wrap.getBoundingClientRect();
    // canvas pixels === DOM pixels (no CSS transform on canvas), so canvas origin == frame inner origin
    preview.style.width = dispW + 'px';
    preview.style.height = dispH + 'px';
    preview.style.left = (fr.left - wr.left + imgLeftInCanvas) + 'px';
    preview.style.top = (fr.top - wr.top + imgTopInCanvas) + 'px';
    // Show preview only when image extends beyond canvas in any direction
    const overflows = imgLeftInCanvas < 0 || imgTopInCanvas < 0 ||
                      (imgLeftInCanvas + dispW) > cw || (imgTopInCanvas + dispH) > ch;
    preview.style.display = overflows ? 'block' : 'none';
  }

  function hideOverflowPreview() {
    const preview = $('#pe-overflow-preview');
    if (preview) preview.style.display = 'none';
  }

  function showUploadPrompt() {
    const prompt = $('#pe-upload-prompt');
    if (prompt) prompt.classList.remove('pe-upload-prompt--hidden');
  }

  function hideUploadPrompt() {
    const prompt = $('#pe-upload-prompt');
    if (prompt) prompt.classList.add('pe-upload-prompt--hidden');
  }

  function showChangePhotoBtn() {
    const group = document.querySelector('.pe-toolbar__group--change');
    if (group) group.style.display = '';
  }

  // ---- TOOLBAR ----
  function bindToolbar() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pe-tool]');
      if (!btn) return;

      const tool = btn.dataset.peTool;
      switch (tool) {
        case 'text': togglePanel('pe-text-panel'); addTextIfNeeded(); break;
        case 'border': togglePanel('pe-border-panel'); break;
        case 'delete': deleteSelected(); break;
      }
    });
    bindFileInputs();
  }

  // ---- PANELS ----
  function bindPanels() {
    const textInput = $('#pe-text-input');
    const textDone = $('#pe-text-done');
    const textDelete = $('#pe-text-delete');
    const fontSelect = $('#pe-font-select');

    if (textInput) {
      textInput.addEventListener('input', () => {
        const obj = canvas?.getActiveObject();
        if (obj && obj.type === 'i-text') { obj.set('text', textInput.value); canvas.renderAll(); }
      });
    }

    if (textDone) {
      textDone.addEventListener('click', () => {
        hidePanel('pe-text-panel');
        canvas?.discardActiveObject();
        canvas?.renderAll();
      });
    }

    if (textDelete) {
      textDelete.addEventListener('click', () => {
        const obj = canvas?.getActiveObject();
        if (obj && obj.type === 'i-text') { canvas.remove(obj); canvas.renderAll(); }
        hidePanel('pe-text-panel');
      });
    }

    if (fontSelect) {
      fontSelect.addEventListener('change', () => {
        const obj = canvas?.getActiveObject();
        if (obj && obj.type === 'i-text') { obj.set('fontFamily', fontSelect.value); canvas.renderAll(); }
      });
    }

    // Text colors
    const textColors = $('#pe-text-colors');
    if (textColors) {
      textColors.addEventListener('click', (e) => {
        const dot = e.target.closest('.pe-color-dot');
        if (!dot) return;
        textColors.querySelectorAll('.pe-color-dot').forEach((d) => d.classList.remove('pe-color-dot--active'));
        dot.classList.add('pe-color-dot--active');
        const obj = canvas?.getActiveObject();
        if (obj && obj.type === 'i-text') { obj.set('fill', dot.dataset.color); canvas.renderAll(); }
      });
    }

    // Border colors
    const borderColors = $('#pe-border-colors');
    if (borderColors) {
      borderColors.addEventListener('click', (e) => {
        const dot = e.target.closest('.pe-color-dot');
        if (!dot) return;
        borderColors.querySelectorAll('.pe-color-dot').forEach((d) => d.classList.remove('pe-color-dot--active'));
        dot.classList.add('pe-color-dot--active');
        currentBorderColor = dot.dataset.color;
        applyBorderColor(currentBorderColor);
        const swatch = $('#pe-border-swatch');
        if (swatch) swatch.style.background = currentBorderColor;
      });
    }

    // Frame styles UI removed — frame_type comes from metafield (set on btn.dataset.frameType)
  }

  function togglePanel(panelId) {
    const panel = document.getElementById(panelId);
    if (!panel) return;
    const isVisible = panel.style.display !== 'none';
    hideAllPanels();
    if (!isVisible) panel.style.display = '';
  }

  function hidePanel(panelId) {
    const panel = document.getElementById(panelId);
    if (panel) panel.style.display = 'none';
  }

  function hideAllPanels() {
    $$('.pe-panel').forEach((p) => (p.style.display = 'none'));
  }

  // ---- TEXT ----
  function addTextIfNeeded() {
    if (!canvas) return;
    const active = canvas.getActiveObject();
    if (active && active.type === 'i-text') {
      const textInput = $('#pe-text-input');
      if (textInput) textInput.value = active.text;
      return;
    }

    const text = new fabric.IText('Your Text', {
      left: canvas.getWidth() / 2,
      top: canvas.getHeight() * 0.75,
      originX: 'center',
      originY: 'center',
      fontFamily: 'Arial',
      fontSize: 28,
      fill: '#ffffff',
      fontWeight: 'bold',
      textAlign: 'center',
      shadow: '1px 1px 3px rgba(0,0,0,0.5)',
      editable: true,
    });

    attachSnap(text);
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();

    const textInput = $('#pe-text-input');
    if (textInput) textInput.value = text.text;
  }

  function onObjectSelected(e) {
    const obj = e.selected?.[0];
    if (obj && obj.type === 'i-text') {
      const textInput = $('#pe-text-input');
      if (textInput) textInput.value = obj.text;
    }
  }

  function onObjectDeselected() {}

  // ---- DELETE ----
  function deleteSelected() {
    if (!canvas) return;
    const obj = canvas.getActiveObject();
    if (!obj) return;
    if (obj === uploadedImageObj) { uploadedImageObj = null; hasPhoto = false; showUploadPrompt(); hideOverflowPreview(); }
    canvas.remove(obj);
    canvas.discardActiveObject();
    canvas.renderAll();
  }

  // ---- FRAME ----
  function applyFrameStyle(type) {
    const frame = $('#pe-frame');
    if (!frame) return;
    frame.className = 'pe-frame';
    if (type !== 'none') frame.classList.add('pe-frame--' + type);
  }

  function applyBorderColor(color) {
    const frame = $('#pe-frame');
    if (!frame) return;
    frame.style.borderColor = color;
  }

  // ---- ORIENTATION ----
  function setOrientation(orientation) {
    if (orientation !== 'portrait' && orientation !== 'landscape') return;
    if (currentOrientation === orientation) return;
    currentOrientation = orientation;
    resizeCanvas(currentOrientation);
    document.querySelectorAll('.pe-orient-btn').forEach((b) => {
      b.classList.toggle('pe-orient-btn--active', b.dataset.peOrient === orientation);
    });
  }

  function bindOrientationButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.pe-orient-btn');
      if (!btn) return;
      e.preventDefault();
      setOrientation(btn.dataset.peOrient);
    });
  }

  // ---- SAVE ----
  function bindSaveButton() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('#pe-save-btn')) return;
      saveAndRedirect();
    });
  }

  function saveAndRedirect() {
    if (!canvas || !currentProductHandle) return;

    // Block save if any required personalization field is missing/invalid
    if (!validatePersonalizationFields()) {
      const host = document.getElementById('pe-personalization');
      const firstBad = host && host.querySelector('.pe-pers__field--invalid');
      if (firstBad) firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    let thumbnail = '';
    let canvasJSON = null;
    // Multi-photo composite: use the currently-loaded slot photos
    if (currentPhotoSlots > 1) {
      // Ensure thumbnail = first filled slot (used by cart drawer)
      thumbnail = uploadedSlots.find(Boolean) || '';
    } else if (canvas) {
      thumbnail = canvas.toDataURL({ format: 'png', quality: 0.9, multiplier: 1 });
      canvasJSON = canvas.toJSON();
    }

    const data = {
      handle: currentProductHandle,
      thumbnail: thumbnail,
      canvasState: canvasJSON,
      frameType: currentFrameType,
      borderColor: currentBorderColor,
      orientation: currentOrientation,
      photoSlots: currentPhotoSlots,
      photoLayout: currentPhotoLayout,
      slotThumbnails: currentPhotoSlots > 1 ? uploadedSlots.slice() : null,
      personalization: Object.assign({}, personalizationValues),
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(STORAGE_KEY + currentProductHandle, JSON.stringify(data));
    } catch (err) {
      console.warn('PE: localStorage save failed', err);
    }

    closeEditor();

    /* Never redirect or reload after save. Any preview on the current page
       (customizer preview, product media gallery, collection card) listens
       for this event and updates itself in place from localStorage. */
    document.dispatchEvent(new CustomEvent('pe:saved', {
      detail: {
        handle: currentProductHandle,
        thumbnail: thumbnail,
        frameType: currentFrameType,
        borderColor: currentBorderColor,
        orientation: currentOrientation,
        personalization: Object.assign({}, personalizationValues),
      }
    }));
  }

  // ---- LOAD STATE ----
  function loadSavedState() {
    if (!currentProductHandle || !canvas) return;
    try {
      const saved = localStorage.getItem(STORAGE_KEY + currentProductHandle);
      if (!saved) return;
      const data = JSON.parse(saved);

      if (data.orientation && data.orientation !== currentOrientation) {
        currentOrientation = data.orientation;
        resizeCanvas(currentOrientation);
        document.querySelectorAll('.pe-orient-btn').forEach((b) => {
          b.classList.toggle('pe-orient-btn--active', b.dataset.peOrient === currentOrientation);
        });
      }

      // frameType ignored when restoring — it now always comes from the product metafield

      if (data.borderColor) {
        currentBorderColor = data.borderColor;
        applyBorderColor(currentBorderColor);
        const swatch = $('#pe-border-swatch');
        if (swatch) swatch.style.background = currentBorderColor;
        $$('#pe-border-colors .pe-color-dot').forEach((d) => d.classList.toggle('pe-color-dot--active', d.dataset.color === currentBorderColor));
      }

      if (data.canvasState) {
        canvas.loadFromJSON(data.canvasState, () => {
          canvas.renderAll();
          canvas.getObjects().forEach((obj) => {
            if (obj.type === 'image') {
              uploadedImageObj = obj;
              hasPhoto = true;
              hideUploadPrompt();
              showChangePhotoBtn();
            }
          });
        });
      }
    } catch (err) {}
  }

  // ---- INIT ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
